import { FC } from 'react';
import { Box, Stack, useMediaQuery } from '@mui/material';

import { useAuth } from 'providers/auth-provider';
import { useRedirectAndScroll } from 'hooks';
import HomeCarousel from 'components/HomeCarousel';
import { isVac } from "utilities/is-vac";
import { vacHomeLabel } from 'utilities/vac-copy';
import { VacLeafRow } from 'components/VacLeafRow';
import { Helmet } from 'react-helmet-async';
import { Language } from 'providers/translates/enums';

import styles from './styles.module.scss';
import { useTranslates } from 'providers/translates';

interface HomeProps {}

const translationsHelmet = {
  descriptionEN: 'Create a lasting tribute for a Veteran. Share stories, photos, memories, and more in a quilt square that becomes part of the Saluting Service national quilt. Browse quilts. Explore resources.',
  descriptionFR: 'Créez un hommage durable à une vétérane ou un vétéran Partagez des anecdotes, des photos, des souvenirs et bien d’autres choses encore dans un carreau de courtepointe qui fera partie de la courtepointe nationale du site Saluons leur service. Parcourir les carreaux. Explorer les ressources.',
  titleEN: 'Saluting Service | Create a Free Online Tribute',
  titleFR: 'Saluons leur service | Créez gratuitement un hommage en ligne'
} as const;

const Home: FC<HomeProps> = () => {
  const { translates: t, selectedLang } = useTranslates();
  const isFrench = selectedLang === Language.FRENCH;
  const { redirectAndScroll } = useRedirectAndScroll({});
  const { isAuthorized, user } = useAuth();
  const mediaMax1350 = useMediaQuery('(max-width:1350px)');
          
  const handleRedirectHonour = () => (isAuthorized && !user?.isInvitedUser) ? redirectAndScroll('/create?step=1') : redirectAndScroll('/signup');

  return (
    <>
    {isVac() && (
      <Helmet>
        <title>{isFrench ? translationsHelmet?.titleFR : translationsHelmet?.titleEN}</title>
        <meta name="description" content={isFrench ? translationsHelmet?.descriptionFR : translationsHelmet?.descriptionEN} />        
      </Helmet>
    )}
    <Box
      style={isVac() ? {
        background: 'center / cover no-repeat url("/img/general/bg-Quilt.png")',
        backgroundRepeat: 'repeat',
        minHeight: '100%',
      } : {
        background: 'center / cover no-repeat url("/img/general/background-preview.png")',
        height: '100%',
      }}
      pb={8}
    >
      <HomeCarousel />

      <div className="center" style={{ marginBottom: '24px' }}>
        <Box mt={7} width={mediaMax1350 ? 'auto' : 850}>
        {!isVac() ? (<span className={styles.titleNormal}>{t?.home?.title ?? 'A place to honour, a place to remember'}</span>) : (<span className={styles.titleNormal}>{vacHomeLabel(selectedLang, t?.home?.title, 'title', '')}</span>)}
        </Box>

        <Stack
          direction={mediaMax1350 ? 'column' : 'row'}
          gap={mediaMax1350 ? 10 : 0}
          justifyContent="space-between"
          sx={{
            marginTop: '14px'
          }}
        >
        <Box width={mediaMax1350 ? '100%' : '60%'}>
          <div className={styles.text22}>
            {!isVac() ? (
              t?.home?.introductionItems ? (
                t?.home.introductionItems.map(item => <div key={item.id}>{item.text}</div>)
              ) : (
                <>
                  <div>
                    When someone important to you dies, the grief can feel 
                    overwhelming. Finding ways to honour their memory can help.  
                  </div>
                  <div>
                    Like a quilt made of fabric with varied pieces sewn with care, 
                    the Canadian Healing Quilt stitches together life stories to provide comfort, healing, and serve as a lasting remembrance. 
                  </div>
                  <div>In Indigenous culture, star blankets and quilts offer protection in life and honour those who have died.</div>
                  <div>
                    Create a lasting tribute for someone important to you. Share stories, photos, cherished memories, and more in a 
                    memorial quilt square that becomes part of the Canadian Healing Quilt. There is no cost to create or maintain a memorial. 
                  </div>
                  <div>
                    Like a favourite quilt you wrap around yourself, the Canadian Healing Quilt can be opened at any time and shared with 
                    those who need comfort.
                  </div>
                  <div>
                    No one we remember is truly gone. Honour their memory and create a memorial quilt square. 
                  </div>
                </>
              )
            ) : (
              t?.home?.introductionItems ? (
                t?.home.introductionItems.map(item => <div key={item.id}>{item.text}</div>)
              ) : (<></>)
            )}
          </div>
          {!isVac() && !mediaMax1350 && (
            <div className={styles.textBottom}>
              <span>
                {t?.home?.poemBlock ?? (
                  <>
                    I am a poet...
                    <br />
                    Poets write on the breathe of a tree’s parched paper.
                    <br />
                    The tree is the ancestral knowledge keeper
                    <br />
                    Of all our relatives
                    <br />
                    Each branch extending to the universe
                    <br />
                    Breathing out prayer
                    <br />
                    Leaves open to the heavens.
                    <br />
                    This sapling
                    <br />
                    Is for those who’ve left,
                    <br />
                    For us still rooted to the earth.
                    <br />
                    • Louise Halfe – “Sky Dancer”
                    <br />
                  </>
                )}
              </span>
            </div>
          )}
        </Box>

          <Stack
            className={styles.wrapperInstruction}
            spacing={9}
            justifyContent={mediaMax1350 ? 'center' : 'end'}
            alignItems={mediaMax1350 ? 'center' : ''}
            alignSelf={mediaMax1350 ? 'unset' : 'flex-start'}
          >
            <div className={styles.wrapperInstructionBg}>
              <div className={styles.instruction} onClick={handleRedirectHonour}>
              {isVac() ? (
                  <img
                    className={styles.instructionImg}
                    src="/img/general/Icon-Salute-RCMP.png"
                    alt="text-size"
                    style={{
                      top: '-45px',
                    }}
                    />
                ) : (
                    <img
                    className={styles.instructionImg}
                    src="/img/general/dove-icon.png"
                    alt="text-size"
                    style={{
                      top: '-30px',
                    }}
                    />
                )
              }
                
                {vacHomeLabel(selectedLang, t?.home?.createQuiltText, 'createQuiltText', 'Honour someone with a memorial quilt square')}
              </div>

              <div className={styles.instruction} onClick={() => redirectAndScroll('/search')}>
              {isVac() ? (
                    <img
                      className={styles.instructionImg}
                      src="/img/general/Icon-Maple Leaf.png"
                      alt="text-size"
                      style={{
                        top: '-35px',
                      }}
                    />
                    ) : (
                      <img
                      className={styles.instructionImg}
                      src="/img/general/heart-icon.png"
                      alt="text-size"
                      style={{
                        top: '-22px',
                      }}
                    />

                    )
                }
                {vacHomeLabel(selectedLang, t?.home?.searchQuiltText, 'searchQuiltText', 'Search for a person to share a condolence, memory, light a candle')}
              </div>

              {/* <div className={styles.instruction}>
                <img className={styles.instructionImg} src="/img/general/candle-icon.png" alt="text-size" style={{
                  top: '-50px'
                }}/>
                <Stack spacing={1} alignItems="center">
                  <span>Send a wind mail</span>
                  <img src="/img/general/question-icon.png" alt="text-size" width={24} height={24}/>
                </Stack>
              </div> */}

              <a className={styles.instruction} href={'/resources'}>
                <div>
                  <img
                    className={styles.instructionImg}
                    src="/img/general/support-icon.png"
                    alt="text-size"
                    style={{
                      top: '-38px',
                      background: 'white',
                      borderRadius: '50%',
                    }}
                  />
                    {vacHomeLabel(selectedLang, t?.home?.resourcesText, 'resourcesText', 'Find grief support')}
                </div>
              </a>
            </div>
            {isVac() && <VacLeafRow />}
          </Stack>          
        </Stack>

        {mediaMax1350 && (
          <div className={styles.textBottom}>
            <span>
              {t?.home?.poemBlock ?? (
                <>
                  I am a poet...
                  <br />
                  Poets write on the breathe of a tree’s parched paper.
                  <br />
                  The tree is the ancestral knowledge keeper
                  <br />
                  Of all our relatives
                  <br />
                  Each branch extending to the universe
                  <br />
                  Breathing out prayer
                  <br />
                  Leaves open to the heavens.
                  <br />
                  This sapling
                  <br />
                  Is for those who’ve left,
                  <br />
                  For us still rooted to the earth.
                  <br />
                  • Louise Halfe – “Sky Dancer”
                  <br />
                </>
              )}
            </span>
          </div>
        )}
      </div>
    </Box>
    </>
  );
};

export default Home;
